// src/hooks/useBossBattleEngine.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Boss battle engine for Level-10
 * - Doron HP decreases per correct note (visual), but Doron is defeated ONLY on full melody completion.
 * - If user takes > timeLimitMs before the NEXT correct note, Doron hits the user.
 * - Wrong note can hit user (configurable) after the first correct note.
 * - User has userMaxHP hits.
 * - First-note grace: NO penalties until the first correct note is played.
 * - Input mode can change timeout rules (mic vs virtual).
 *
 * ✅ FIX: crypto.randomUUID is not supported on many Android browsers/WebViews.
 * ✅ ALSO: avoid globalThis (ESLint no-undef). Use window.crypto safely.
 */

function safeUUID() {
  try {
    // Browser-safe check (no globalThis)
    if (
      typeof window !== "undefined" &&
      window.crypto &&
      typeof window.crypto.randomUUID === "function"
    ) {
      return window.crypto.randomUUID();
    }
  } catch {}

  // Fallback: sufficiently unique for UI IDs
  return `id_${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random()
    .toString(16)
    .slice(2)}`;
}

export default function useBossBattleEngine({
  totalNotes,
  enabled = true,

  // Default (virtual) time limit:
  timeLimitMs = 3000,

  // Optional mic time limit:
  micTimeLimitMs = 4000,

  userMaxHP = 7,
  doronMaxHP = 100,
  doronMinHpBeforeDefeat = 1, // keep Doron at >=1 until full completion
  sleepMs = 1200, // Twinkle sleep break moment duration
}) {
  const [doronHP, setDoronHP] = useState(doronMaxHP);
  const [userHP, setUserHP] = useState(userMaxHP);

  const [projectiles, setProjectiles] = useState([]); // up attacks
  const [fireballs, setFireballs] = useState([]); // down attacks

  const [twinkleSleeping, setTwinkleSleeping] = useState(false);

  // Which input mode is currently active for timing?
  // "virtual" => uses timeLimitMs
  // "mic"     => uses micTimeLimitMs
  const [inputMode, setInputMode] = useState("virtual");

  // ✅ First-note grace: no penalties until first correct note
  const firstCorrectPlayedRef = useRef(false);

  // We measure "time since last progress action"
  const lastActionAtRef = useRef(Date.now());

  const doronDamagePerNote = useMemo(() => {
    if (!totalNotes || totalNotes <= 0) return 0;
    return doronMaxHP / totalNotes;
  }, [doronMaxHP, totalNotes]);

  const spawnProjectile = useCallback((type = "note") => {
    const id = safeUUID();
    setProjectiles((p) => [...p, { id, type }]);
    setTimeout(() => {
      setProjectiles((p) => p.filter((x) => x.id !== id));
    }, 650);
  }, []);

  const spawnFireball = useCallback(() => {
    const id = safeUUID();
    setFireballs((f) => [...f, { id }]);
    setTimeout(() => {
      setFireballs((f) => f.filter((x) => x.id !== id));
    }, 650);
  }, []);

  const markActionNow = useCallback(() => {
    lastActionAtRef.current = Date.now();
  }, []);

  const hitUser = useCallback(() => {
    spawnFireball();
    setUserHP((hp) => Math.max(0, hp - 1));
    markActionNow();
  }, [spawnFireball, markActionNow]);

  const hitDoron = useCallback(() => {
    spawnProjectile("note");
    setDoronHP((hp) => {
      const next = hp - doronDamagePerNote;
      return Math.max(doronMinHpBeforeDefeat, next);
    });
    markActionNow();
  }, [spawnProjectile, doronDamagePerNote, doronMinHpBeforeDefeat, markActionNow]);

  const getActiveTimeLimitMs = useCallback(() => {
    if (inputMode === "mic") return micTimeLimitMs ?? timeLimitMs;
    return timeLimitMs;
  }, [inputMode, micTimeLimitMs, timeLimitMs]);

  // ⏱ Timer punishment: only after FIRST correct note has been played
  useEffect(() => {
    if (!enabled) return;
    if (twinkleSleeping) return;

    const t = setInterval(() => {
      if (userHP <= 0) return;

      // ✅ First-note grace
      if (!firstCorrectPlayedRef.current) return;

      const elapsed = Date.now() - lastActionAtRef.current;
      const limit = getActiveTimeLimitMs();

      if (elapsed > limit) {
        hitUser();
      }
    }, 140);

    return () => clearInterval(t);
  }, [enabled, hitUser, userHP, twinkleSleeping, getActiveTimeLimitMs]);

  const onCorrectNote = useCallback(() => {
    if (!enabled) return;
    if (twinkleSleeping) return;
    if (userHP <= 0) return;

    if (!firstCorrectPlayedRef.current) firstCorrectPlayedRef.current = true;

    hitDoron();
  }, [enabled, twinkleSleeping, userHP, hitDoron]);

  const onWrongNote = useCallback(() => {
    if (!enabled) return;
    if (twinkleSleeping) return;
    if (userHP <= 0) return;

    if (!firstCorrectPlayedRef.current) return;

    hitUser();
  }, [enabled, twinkleSleeping, userHP, hitUser]);

  const onFullMelodyComplete = useCallback(() => {
    setDoronHP(0);
  }, []);

  const resetBattle = useCallback(() => {
    setDoronHP(doronMaxHP);
    setUserHP(userMaxHP);
    setProjectiles([]);
    setFireballs([]);
    setTwinkleSleeping(false);

    firstCorrectPlayedRef.current = false;
    lastActionAtRef.current = Date.now();
  }, [doronMaxHP, userMaxHP]);

  const twinkleSpell = useCallback(async () => {
    setTwinkleSleeping(true);
    await new Promise((r) => setTimeout(r, sleepMs));
    setTwinkleSleeping(false);

    setDoronHP(doronMaxHP);
    setUserHP(userMaxHP);
    setProjectiles([]);
    setFireballs([]);

    firstCorrectPlayedRef.current = false;
    lastActionAtRef.current = Date.now();

    return { resetRequested: true };
  }, [doronMaxHP, userMaxHP, sleepMs]);

  return {
    doronHP,
    doronMaxHP,
    userHP,
    userMaxHP,

    projectiles,
    fireballs,

    twinkleSleeping,

    inputMode,
    setInputMode,

    onCorrectNote,
    onWrongNote,
    onFullMelodyComplete,

    twinkleSpell,
    resetBattle,

    markActionNow,
  };
}
